<template>
    <Dialog v-model:open="opened">
        <DialogTrigger>
            <slot />
        </DialogTrigger>
        <DialogContent>
            <DialogHeader class="items-center">
                <template #icon> <EcomailLogo /></template>
                <DialogTitle>
                    {{
                        integration
                            ? $t("integrations.ecomail.modal.title_edit")
                            : $t("integrations.ecomail.modal.title_add")
                    }}</DialogTitle
                >
            </DialogHeader>
            <div class="px-6 sm:flex sm:items-start">
                <div class="mt-3 sm:mt-0 sm:text-left w-full text-center">
                    <h3
                        id="modal-title"
                        class="text-lg font-medium leading-6 text-iron-900"
                    ></h3>
                    <div class="mt-2">
                        <div class="mt-4 gap-1.5 grid w-full items-center">
                            <Label for="ecomail-name">{{
                                $t("integrations.ecomail.modal.nameLabel")
                            }}</Label>
                            <Input
                                v-model="integrationName"
                                id="ecomail-nam"
                                :placeholder="
                                    $t(
                                        'integrations.ecomail.modal.name_placeholder',
                                    )
                                "
                            />
                        </div>

                        <div class="mt-4 gap-1.5 grid w-full items-center">
                            <Label for="ecomail-apikey">{{
                                $t("integrations.ecomail.modal.apiKeyLabel")
                            }}</Label>
                            <Input
                                v-model="apiKey"
                                id="ecomail-apikey"
                                :placeholder="
                                    $t(
                                        'integrations.ecomail.modal.api_key_placeholder',
                                    )
                                "
                            />
                        </div>
                    </div>
                </div>
            </div>
            <DialogFooter
                class="w-full"
                :class="cn(removable ? '!justify-between' : 'justify-end')"
            >
                <Button
                    v-if="removable"
                    @click="$emit('remove-integration')"
                    variant="destructive-ghost"
                    class="gap-1 flex"
                    size="xs"
                >
                    <Trash :size="16" />
                    {{ $t("integrations.remove_integration") }}
                </Button>

                <div class="flex-end flex">
                    <DialogClose>
                        <Button class="ml-4" variant="outline" type="button">{{
                            $t("cancel")
                        }}</Button>
                    </DialogClose>

                    <Button
                        class="ml-4"
                        type="button"
                        @click="createOrUpdateEcomailIntegration"
                        >{{
                            integration ? $t("save_changes") : $t("add")
                        }}</Button
                    >
                </div>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { cn } from "@/lib/utils";
import { ref } from "vue";
import { router } from "@inertiajs/vue3";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose,
} from "@/components/ui/dialog-v2";
import Button from "@/components/ui/button/Button.vue";
import { Trash } from "lucide-vue-next";
import EcomailLogo from "../logos/simple/Ecomail.svg";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import route from "ziggy-js";

type Integration = {
    name: string;
    type: string;
    id: number;
    data: any;
    errors: any[];
};

const props = defineProps<{
    removable?: boolean;
    integration?: Integration;
}>();

const integrationName = ref(props.integration ? props.integration.name : "");
const apiKey = ref(props.integration ? props.integration?.data?.apiKey : "");
const opened = ref(false);

function create() {
    router.post(
        route("app.ecomail-integrations.store"),
        {
            name: integrationName.value,
            apiKey: apiKey.value,
        },
        {
            onFinish: () => {
                closeModal();
            },
        },
    );
}

function update() {
    router.put(
        route("app.ecomail-integrations.update", {
            integration: props.integration?.id,
        }),
        {
            name: integrationName.value,
            apiKey: apiKey.value,
        },
        {
            onFinish: () => {
                closeModal();
            },
        },
    );
}

function createOrUpdateEcomailIntegration() {
    if (props.integration) {
        update();
    } else {
        create();
    }
}

const closeModal = () => {
    opened.value = false;
};
</script>
